import { ChangeEvent, ReactNode, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import ContractExpiryAlertDialog from "@/components/suppliers/ContractExpiryAlertDialog";
import {
  LogOut,
  Menu,
  Users,
  LayoutDashboard,
  TrendingUp,
  Plane,
  Car,
  Hotel,
  MapPin,
  Ticket,
  FileText,
  ChevronDown,
  FileCheck,
  Plus,
  UserPlus,
  Target,
  Building2,
  Moon,
  Sun,
  Camera,
  Loader2,
  Handshake,
  Truck,
  FolderOpen,
  BriefcaseBusiness,
  CircleUserRound,
  FilePlus2,
  ClipboardList,
  PackageCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useTheme } from "next-themes";
import logo from "@/assets/logo.svg";
import { getBuildLabel } from "@/config/env";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api";

interface DashboardLayoutProps {
  children: ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const { user, userRole, signOut } = useAuth();
  const { toast } = useToast();
  const { resolvedTheme, setTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(user?.profile_photo ?? null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isDarkMode = resolvedTheme === "dark";

  const catalogPaths = ["/airport-services", "/transfers", "/hotels", "/tours", "/attractions", "/additional-request-services", "/settings"];
  const isCatalogRoute = catalogPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  const isAdminRoute = location.pathname === "/create-user";
  const isPartnersAgencyRoute = location.pathname === "/partners" || location.pathname === "/partners/agency";
  const isPartnersOperatorRoute = location.pathname === "/partners/operator";
  const isPartnersFinalClientRoute = location.pathname === "/partners/final-client" || location.pathname === "/partners/final-clients";
  const isPartnersRoute = isPartnersAgencyRoute || isPartnersOperatorRoute || isPartnersFinalClientRoute;
  const isCreateNewProposalRoute = location.pathname === "/create-new-proposal" || location.pathname === "/new-lead";
  const isProposalListRoute = location.pathname.startsWith("/proposals") && !location.pathname.startsWith("/proposals-legacy");
  const isProposalSectionRoute = isCreateNewProposalRoute || isProposalListRoute;
  const legacyPaths = ["/clients", "/proposals-legacy"];
  const isLegacyRoute = legacyPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  const [catalogOpen, setCatalogOpen] = useState(isCatalogRoute);
  const [adminOpen, setAdminOpen] = useState(isAdminRoute);
  const [legacyOpen, setLegacyOpen] = useState(isLegacyRoute);
  const canCreateProposal = userRole === "ceo" || userRole === "general_manager" || userRole === "sales_staff";
  const canManageUsers = userRole === "ceo" || userRole === "general_manager";
  const canSeePartners = userRole === "ceo" || userRole === "general_manager" || userRole === "sales_staff";
  const canSeeLegacyClients = Boolean(userRole);
  const canSeeLegacyProposals = userRole === "ceo" || userRole === "general_manager" || userRole === "sales_staff";
  const canSeeLegacySection = canSeeLegacyClients || canSeeLegacyProposals;
  const isFinanceStaff = userRole === "finance_staff";
  const canSeeProposals = userRole === "sales_staff" || userRole === "finance_staff" || userRole === "ceo" || userRole === "general_manager";
  const canSeeProposalSection = canCreateProposal || canSeeProposals;
  const canSeeBookingPackages = isFinanceStaff || userRole === "sales_staff" || userRole === "ceo" || userRole === "general_manager";
  const canSeeWeeklyReport = isFinanceStaff || userRole === "ceo" || userRole === "general_manager";
  const userName = user?.full_name ?? "User";
  const userInitials = (user?.full_name ?? user?.email ?? "U")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "U";

  useEffect(() => {
    setProfilePhoto(user?.profile_photo ?? null);
  }, [user?.id, user?.profile_photo]);

  const openPhotoPicker = () => {
    if (uploadingPhoto) return;
    photoInputRef.current?.click();
  };

  const handleProfilePhotoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    setUploadingPhoto(true);

    try {
      const response = await apiFetch<{ profile_photo?: string | null }>("/auth/me/profile-photo", {
        method: "PUT",
        body: formData,
      });
      setProfilePhoto(response.profile_photo ?? null);
      toast({
        title: "Photo saved",
        description: "Your profile photo was updated.",
      });
    } catch (error) {
      const message = typeof error === "object" && error !== null && "message" in error
        ? String((error as { message?: string }).message ?? "")
        : "";
      toast({
        title: "Upload failed",
        description: message || "Could not save your profile photo.",
        variant: "destructive",
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const getRoleLabel = (role: string | null) => {
    switch (role) {
      case "ceo":
        return "CEO";
      case "general_manager":
        return "General Manager";
      case "sales_staff":
        return "Sales Staff";
      case "operations_staff":
        return "Operations Staff";
      case "finance_staff":
        return "Finance Staff";
      default:
        return "User";
    }
  };

  return (
    <div className="flex h-screen bg-background">
      <aside
        className={cn(
          "border-r border-border bg-card transition-all duration-300 shrink-0 flex flex-col overflow-hidden",
          sidebarOpen ? "w-64" : "w-0",
        )}
      >
        <div className="flex h-16 items-center border-b border-border px-6 gap-3 flex-shrink-0">
          <img src={logo} alt="High Class Brazil" className="h-8 w-8 flex-shrink-0" />
          <h1 className="text-xl font-bold text-foreground whitespace-nowrap">High Class Brazil</h1>
        </div>
        <div className="p-4 space-y-2 h-[calc(100vh-4rem)] overflow-y-auto pr-2">
          <div className="mb-4 rounded-xl border border-border bg-muted/70 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Avatar className="h-14 w-14 border border-border bg-background">
                  {profilePhoto ? (
                    <AvatarImage src={profilePhoto} alt={userName} className="object-cover" />
                  ) : null}
                  <AvatarFallback className="bg-secondary text-secondary-foreground font-semibold">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={openPhotoPicker}
                  disabled={uploadingPhoto}
                  className="absolute -bottom-1 -right-1 inline-flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60"
                  aria-label="Upload profile photo"
                >
                  {uploadingPhoto ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                </button>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleProfilePhotoChange}
                />
              </div>
              <div className="min-w-0">
                <p className="truncate text-lg font-semibold leading-tight text-foreground">{userName}</p>
                <p className="text-xs text-muted-foreground">{getRoleLabel(userRole)}</p>
              </div>
            </div>
          </div>

          <Button
            variant={location.pathname === "/dashboard" ? "secondary" : "ghost"}
            className="w-full justify-start"
            onClick={() => navigate("/dashboard")}
          >
            <LayoutDashboard className="mr-2 h-4 w-4" />
            Dashboard
          </Button>

          <Button
            variant={location.pathname.startsWith("/prospects") ? "secondary" : "ghost"}
            className="w-full justify-start"
            onClick={() => navigate("/prospects")}
          >
            <Target className="mr-2 h-4 w-4" />
            Prospects
          </Button>

          <Button
            variant={location.pathname.startsWith("/suppliers") ? "secondary" : "ghost"}
            className="w-full justify-start"
            onClick={() => navigate("/suppliers")}
          >
            <Truck className="mr-2 h-4 w-4" />
            Suppliers
          </Button>

          {canSeePartners && (
            <div className="w-full space-y-1">
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start px-4 py-2 font-semibold",
                  isPartnersRoute && "bg-muted text-foreground",
                )}
                onClick={() => navigate("/partners/agency")}
              >
                <Handshake className="mr-2 h-4 w-4" />
                Partners
              </Button>
              <div className="ml-5 space-y-1 border-l border-border/70 pl-3">
                <Button
                  variant={isPartnersAgencyRoute ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => navigate("/partners/agency")}
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  Agency
                </Button>
                <Button
                  variant={isPartnersOperatorRoute ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => navigate("/partners/operator")}
                >
                  <BriefcaseBusiness className="mr-2 h-4 w-4" />
                  Operator
                </Button>
                <Button
                  variant={isPartnersFinalClientRoute ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => navigate("/partners/final-client")}
                >
                  <CircleUserRound className="mr-2 h-4 w-4" />
                  Final Client
                </Button>
              </div>
            </div>
          )}

          {canSeeProposalSection && (
            <div className="w-full space-y-1">
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start px-4 py-2 font-semibold",
                  isProposalSectionRoute && "bg-muted text-foreground",
                )}
                onClick={() => navigate("/proposals")}
              >
                <FolderOpen className="mr-2 h-4 w-4" />
                Proposals
              </Button>
              <div className="ml-5 space-y-1 border-l border-border/70 pl-3">
                {canCreateProposal && (
                  <Button
                    variant={isCreateNewProposalRoute ? "secondary" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => navigate("/create-new-proposal")}
                  >
                    <FilePlus2 className="mr-2 h-4 w-4" />
                    New Proposal
                  </Button>
                )}

                {canSeeProposals && (
                  <Button
                    variant={isProposalListRoute ? "secondary" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => navigate("/proposals")}
                  >
                    <ClipboardList className="mr-2 h-4 w-4" />
                    All Proposals
                  </Button>
                )}
              </div>
            </div>
          )}

          {canSeeBookingPackages && (
            <Button
              variant={location.pathname.startsWith("/booking-packages") ? "secondary" : "ghost"}
              className="w-full justify-start"
              onClick={() => navigate("/booking-packages")}
            >
              <PackageCheck className="mr-2 h-4 w-4" />
              Booking Packages
            </Button>
          )}

          {canSeeWeeklyReport && (
            <Button
              variant={location.pathname.startsWith("/reports/weekly-sales") ? "secondary" : "ghost"}
              className="w-full justify-start"
              onClick={() => navigate("/reports/weekly-sales")}
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              Weekly Report
            </Button>
          )}

          {canSeeLegacySection && (
            <Collapsible open={legacyOpen} onOpenChange={setLegacyOpen} className="w-full space-y-2">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between px-4 py-2 hover:bg-muted">
                  <span className="text-sm font-semibold text-foreground">Legacy</span>
                  <ChevronDown className={cn("h-4 w-4 transition-transform", legacyOpen && "rotate-180")} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1 pl-2">
                {canSeeLegacyClients && (
                  <Button
                    variant={location.pathname === "/clients" ? "secondary" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => navigate("/clients")}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Clients (Legacy)
                  </Button>
                )}

                {canSeeLegacyProposals && (
                  <Button
                    variant={location.pathname.startsWith("/proposals-legacy") ? "secondary" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => navigate("/proposals-legacy")}
                  >
                    <FileCheck className="mr-2 h-4 w-4" />
                    Proposals (Legacy)
                  </Button>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}


          <Collapsible open={catalogOpen} onOpenChange={setCatalogOpen} className="w-full space-y-2">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between px-4 py-2 hover:bg-muted">
                  <span className="text-sm font-semibold text-foreground">Service Catalog</span>
                  <ChevronDown className={cn("h-4 w-4 transition-transform", catalogOpen && "rotate-180")} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1 pl-2">
                <Button
                  variant={location.pathname === "/airport-services" ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => navigate("/airport-services")}
                >
                  <Plane className="mr-2 h-4 w-4" />
                  Airport Services
                </Button>

                <Button
                  variant={location.pathname === "/transfers" ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => navigate("/transfers")}
                >
                  <Car className="mr-2 h-4 w-4" />
                  Transfers
                </Button>

                <Button
                  variant={location.pathname === "/hotels" ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => navigate("/hotels")}
                >
                  <Hotel className="mr-2 h-4 w-4" />
                  Hotels
                </Button>

                <Button
                  variant={location.pathname === "/tours" ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => navigate("/tours")}
                >
                  <MapPin className="mr-2 h-4 w-4" />
                  Tours
                </Button>

                <Button
                  variant={location.pathname === "/attractions" ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => navigate("/attractions")}
                >
                  <Ticket className="mr-2 h-4 w-4" />
                  Attractions
                </Button>

                <Button
                  variant={location.pathname === "/additional-request-services" ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => navigate("/additional-request-services")}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Additional Requests
                </Button>

                <Button
                  variant={location.pathname === "/settings" ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => navigate("/settings")}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Terms
                </Button>
              </CollapsibleContent>
            </Collapsible>
            {canManageUsers && (
              <Collapsible open={adminOpen} onOpenChange={setAdminOpen} className="w-full space-y-2">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between px-4 py-2 hover:bg-muted">
                    <span className="text-sm font-semibold text-foreground">User Management</span>
                    <ChevronDown className={cn("h-4 w-4 transition-transform", adminOpen && "rotate-180")} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1 pl-2">
                  <Button
                    variant={location.pathname === "/create-user" ? "secondary" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => navigate("/create-user")}
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    Create User
                  </Button>
                </CollapsibleContent>
              </Collapsible>
            )}
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex h-16 items-center justify-between border-b border-border bg-card px-6">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setTheme(isDarkMode ? "light" : "dark")}
            >
              {isDarkMode ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
              {isDarkMode ? "Light Mode" : "Dark Mode"}
            </Button>
            <Button variant="outline" size="sm" onClick={signOut}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6">
          {children}
        </main>
        <footer className="shrink-0 border-t border-border px-6 py-1.5 text-center">
          <span className="text-[10px] text-muted-foreground">{getBuildLabel()}</span>
        </footer>
      </div>

      {/* Expiring supplier contracts. Renders nothing unless the viewer is CEO or General
          Manager and has undismissed alerts, so it can sit on every authenticated screen. */}
      <ContractExpiryAlertDialog />
    </div>
  );
};

export default DashboardLayout;
